import type { TemplateRegistration, TemplateData } from './types';
import { sanitizeText, sanitizeSeoText, escapeAttr } from './sanitize';
import { getIconSvg, renderStars, buildFontsUrl, buildNavLinks, NavDataFlags, truncateAtSentence } from './helpers';

export const darkElegance: TemplateRegistration = {
  meta: {
    id: 'dark-elegance',
    name: 'Dark Elegance',
    category: 'Classic',
    description: 'Refined dark mode design with deep purple and gold accents for premium brands',
    previewImage: 'https://images.unsplash.com/photo-1618221195710-dd6b41faaea6?w=800&q=80',
    supportedFeatures: ['contact-form', 'phone-display', 'address-map', 'business-hours', 'about-section', 'services-list', 'testimonials', 'google-reviews', 'social-links', 'faq', 'price-list', 'promotions', 'photo-gallery'],
    defaultEnabledFeatures: ['contact-form', 'phone-display', 'about-section', 'services-list', 'testimonials'],
  },
  render(data: TemplateData, enabledFeatures: Set<string>): string {
    const safeName = sanitizeText(data.businessName);
    const safeHeadline = sanitizeText(data.headline);
    const safeAbout = sanitizeText(data.aboutText);
    const safeSeoTitle = sanitizeSeoText(data.seo.title, 70);
    const safeSeoDesc = sanitizeSeoText(data.seo.description, 160);

    const phone = data.businessData?.phone || null;
    const contactEmail = data.businessData?.contactEmail || null;
    const rating = data.businessData?.rating || null;
    const reviewCount = data.businessData?.reviewCount || 0;
    const businessHours = data.businessData?.businessHours || [];
    const photos = data.businessData?.photos || [];
    const address = data.businessData?.address || '';
    const googleMapsUrl = data.businessData?.googleMapsUrl || null;
    const heroImage = photos[0] || 'https://images.unsplash.com/photo-1618221195710-dd6b41faaea6?w=1600&q=80';
    const aboutImage = photos[1] || null;
    const galleryImages = photos.slice(2, 7);

    const navDataFlags: NavDataFlags = {
      hasServices: data.services.length > 0,
      hasTestimonials: data.testimonials.length > 0,
      hasGalleryPhotos: galleryImages.length > 0,
      hasFaq: !!(data.faq && data.faq.length > 0),
      hasPriceList: !!(data.priceList && data.priceList.length > 0),
    };
    const navLinks = buildNavLinks(enabledFeatures, navDataFlags);
    const fontsUrl = buildFontsUrl(['Bodoni Moda', 'EB Garamond']);

    const sections: string[] = [];

    // --- Google Reviews ---
    if (enabledFeatures.has('google-reviews') && rating) {
      sections.push(`
      <section class="google-reviews">
        <div class="container">
          <div class="review-badge">
            <div class="review-stars">${renderStars(Math.round(rating))}</div>
            <p class="review-text">${rating.toFixed(1)} out of 5 &bull; ${reviewCount} reviews</p>
          </div>
        </div>
      </section>`);
    }

    // --- Promotions ---
    if (enabledFeatures.has('promotions') && data.promotion) {
      const promoTitle = sanitizeText(data.promotion.title);
      const promoDesc = sanitizeText(data.promotion.description);
      const promoDiscount = sanitizeText(data.promotion.discount);
      sections.push(`
      <section class="promo-banner">
        <div class="container">
          <div class="promo-inner">
            <span class="promo-ornament">&diams;</span>
            <span class="promo-discount">${promoDiscount}</span>
            <span class="promo-ornament">&diams;</span>
          </div>
          <p class="promo-title">${promoTitle}</p>
          <p class="promo-desc">${promoDesc}</p>
        </div>
      </section>`);
    }

    // --- About ---
    if (enabledFeatures.has('about-section')) {
      sections.push(`
      <section id="about" class="de-section" style="padding:0;">
        <div class="about-fullbleed">
          ${aboutImage ? `<div class="about-fullbleed-bg"><img src="${escapeAttr(aboutImage)}" alt="${safeName}" loading="lazy"></div>` : ''}
          <div class="about-fullbleed-overlay"></div>
          <div class="container" style="position:relative;z-index:1;padding:100px 24px;">
            <div class="about-content">
              <span class="label-tag">About Us</span>
              <div class="gold-ornament"><span></span><span></span><span></span></div>
              <h2>${safeName}</h2>
              <p class="about-text">${safeAbout}</p>
            </div>
          </div>
        </div>
      </section>`);
    }

    // --- Services ---
    if (enabledFeatures.has('services-list') && data.services.length > 0) {
      const serviceItems = data.services.map(s => {
        const sName = sanitizeText(s.name);
        const sDesc = sanitizeText(s.description);
        return `
          <div class="service-row">
            <div class="service-icon-area">
              <div class="service-icon-diamond">${getIconSvg(s.icon || 'star')}</div>
            </div>
            <div class="service-details">
              <h3>${sName}</h3>
              <p>${sDesc}</p>
              ${s.price ? `<span class="service-price">${sanitizeText(s.price)}</span>` : ''}
            </div>
          </div>`;
      }).join('');
      sections.push(`
      <section id="services" class="de-section de-section-alt">
        <div class="container">
          <span class="label-tag">Our Services</span>
          <div class="gold-ornament"><span></span><span></span><span></span></div>
          <h2 class="section-title">What We Offer</h2>
          <div class="services-list">${serviceItems}</div>
        </div>
      </section>`);
    }

    // --- Photo Gallery ---
    if (enabledFeatures.has('photo-gallery') && galleryImages.length > 0) {
      const imgs = galleryImages.map(url => `<div class="gallery-cell"><img src="${escapeAttr(url)}" alt="${safeName}" loading="lazy"></div>`).join('');
      sections.push(`
      <section id="gallery" class="de-section">
        <div class="container">
          <span class="label-tag">Gallery</span>
          <div class="gold-ornament"><span></span><span></span><span></span></div>
          <div class="gallery-masonry">${imgs}</div>
        </div>
      </section>`);
    }

    // --- Testimonials ---
    if (enabledFeatures.has('testimonials') && data.testimonials.length > 0) {
      const quotes = data.testimonials.map(t => {
        const tName = sanitizeText(t.name);
        const tContent = sanitizeText(t.content);
        const tRole = t.role ? sanitizeText(t.role) : '';
        return `
          <div class="quote-block">
            <div class="quote-decorative">&ldquo;</div>
            <blockquote>${tContent}</blockquote>
            <div class="quote-decorative-end">&rdquo;</div>
            <div class="quote-author">
              <strong>${tName}</strong>
              ${tRole ? `<span>${tRole}</span>` : ''}
              ${t.rating ? `<div class="q-stars">${renderStars(t.rating)}</div>` : ''}
            </div>
          </div>`;
      }).join('');
      sections.push(`
      <section id="testimonials" class="de-section de-section-alt">
        <div class="container">
          <span class="label-tag">Testimonials</span>
          <div class="gold-ornament"><span></span><span></span><span></span></div>
          <h2 class="section-title">Words of Praise</h2>
          <div class="testimonials-carousel">${quotes}</div>
        </div>
      </section>`);
    }

    // --- FAQ ---
    if (enabledFeatures.has('faq') && data.faq && data.faq.length > 0) {
      const items = data.faq.map(f => {
        const q = sanitizeText(f.question);
        const a = sanitizeText(f.answer);
        return `
          <div class="faq-entry">
            <h3><span class="faq-q">Q.</span> ${q}</h3>
            <p>${a}</p>
          </div>`;
      }).join('');
      sections.push(`
      <section id="faq" class="de-section">
        <div class="container">
          <span class="label-tag">FAQ</span>
          <div class="gold-ornament"><span></span><span></span><span></span></div>
          <div class="faq-grid">${items}</div>
        </div>
      </section>`);
    }

    // --- Price List ---
    if (enabledFeatures.has('price-list') && data.priceList && data.priceList.length > 0) {
      const cards = data.priceList.map(p => {
        const pName = sanitizeText(p.name);
        const pDesc = sanitizeText(p.description);
        const pPrice = sanitizeText(p.price);
        return `
          <div class="price-card">
            <div class="price-card-gold-top"></div>
            <h3>${pName}</h3>
            <p class="price-card-desc">${pDesc}</p>
            <span class="price-card-amount">${pPrice}</span>
          </div>`;
      }).join('');
      sections.push(`
      <section id="pricing" class="de-section de-section-alt">
        <div class="container">
          <span class="label-tag">Pricing</span>
          <div class="gold-ornament"><span></span><span></span><span></span></div>
          <h2 class="section-title">Our Rates</h2>
          <div class="pricing-grid">${cards}</div>
        </div>
      </section>`);
    }

    // --- Business Hours ---
    if (enabledFeatures.has('business-hours') && businessHours.length > 0) {
      const hoursList = businessHours.map(h => `<li>${sanitizeText(h)}</li>`).join('');
      sections.push(`
      <section class="de-section">
        <div class="container">
          <span class="label-tag">Hours</span>
          <div class="gold-ornament"><span></span><span></span><span></span></div>
          <ul class="hours-list">${hoursList}</ul>
        </div>
      </section>`);
    }

    // --- Contact ---
    if (enabledFeatures.has('contact-form')) {
      sections.push(`
      <section id="contact" class="de-section de-section-alt">
        <div class="container">
          <span class="label-tag">Contact</span>
          <div class="gold-ornament"><span></span><span></span><span></span></div>
          <h2 class="section-title">Reach Out</h2>
          <div class="contact-grid">
            <div class="contact-info">
              ${enabledFeatures.has('phone-display') && phone ? `<div class="contact-row">${getIconSvg('phone')}<a href="tel:${escapeAttr(phone)}">${sanitizeText(phone)}</a></div>` : ''}
              ${enabledFeatures.has('address-map') && address ? `<div class="contact-row">${getIconSvg('map-pin')}<span>${sanitizeText(address)}</span></div>` : ''}
              ${enabledFeatures.has('address-map') && googleMapsUrl ? `<a class="map-btn" href="${escapeAttr(googleMapsUrl)}" target="_blank" rel="noopener noreferrer">View on Map &rarr;</a>` : ''}
            </div>
            <form class="de-form" ${contactEmail ? `action="https://formsubmit.co/${escapeAttr(contactEmail)}" method="POST"` : 'onsubmit="return false;"'}>
              <input type="text" placeholder="Your Name" required>
              <input type="email" placeholder="Email" required>
              <textarea placeholder="Message" rows="5" required></textarea>
              <button type="submit">Send Inquiry</button>
            </form>
          </div>
        </div>
      </section>`);
    }

    return `<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="UTF-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <title>${safeSeoTitle}</title>
  <meta name="description" content="${safeSeoDesc}">
  <link rel="preconnect" href="https://fonts.googleapis.com">
  <link rel="preconnect" href="https://fonts.gstatic.com" crossorigin>
  <link href="${fontsUrl}" rel="stylesheet">
  <style>
    *, *::before, *::after { margin: 0; padding: 0; box-sizing: border-box; }
    :root {
      --bg: #0c0015; --bg-alt: #120020; --purple: #7e22ce; --purple-dim: rgba(126,34,206,.15);
      --gold: #eab308; --gold-dim: rgba(234,179,8,.2); --text: #f3e8ff; --text-muted: #c4b5d0;
      --font-heading: 'Bodoni Moda', serif; --font-body: 'EB Garamond', serif;
    }
    html { scroll-behavior: smooth; }
    body { font-family: var(--font-body); background: var(--bg); color: var(--text); line-height: 1.75; font-size: 18px; }
    .container { max-width: 1120px; margin: 0 auto; padding: 0 24px; }
    a { color: var(--gold); text-decoration: none; transition: opacity .2s; }
    a:hover { opacity: .8; }
    img { max-width: 100%; display: block; }

    /* NAV */
    .de-nav { position: fixed; top: 0; left: 0; right: 0; z-index: 100; background: rgba(12,0,21,.92); backdrop-filter: blur(16px); border-bottom: 1px solid var(--purple-dim); }
    .de-nav-inner { display: flex; align-items: center; justify-content: space-between; padding: 18px 24px; max-width: 1120px; margin: 0 auto; }
    .de-nav-brand { font-family: var(--font-heading); font-size: 1.2rem; color: var(--gold); text-transform: uppercase; letter-spacing: 5px; }
    .de-nav-links { display: flex; gap: 28px; list-style: none; }
    .de-nav-links a { color: var(--text-muted); font-family: var(--font-heading); font-size: .72rem; text-transform: uppercase; letter-spacing: 2.5px; transition: color .2s; }
    .de-nav-links a:hover { color: var(--gold); }
    .de-nav-toggle { display: none; background: none; border: none; cursor: pointer; }
    .de-nav-toggle span { display: block; width: 22px; height: 1px; background: var(--gold); margin: 5px 0; }

    /* HERO - letterbox cinematic */
    .de-hero { position: relative; min-height: 100vh; display: flex; flex-direction: column; overflow: hidden; }
    .de-hero-bar { background: #000; flex-shrink: 0; }
    .de-hero-bar-top { height: 12vh; }
    .de-hero-bar-bottom { height: 12vh; }
    .de-hero-middle { flex: 1; position: relative; display: flex; align-items: center; overflow: hidden; }
    .de-hero-img { position: absolute; inset: 0; }
    .de-hero-img img { width: 100%; height: 100%; object-fit: cover; }
    .de-hero-overlay { position: absolute; inset: 0; background: linear-gradient(to right, rgba(12,0,21,.8) 0%, rgba(12,0,21,.3) 60%, transparent 100%); z-index: 1; }
    .de-hero-content { position: relative; z-index: 2; max-width: 1120px; margin: 0 auto; width: 100%; padding: 40px 24px; }
    .de-hero h1 { font-family: var(--font-heading); font-size: clamp(2.4rem, 5.5vw, 4.2rem); font-weight: 400; line-height: 1.15; max-width: 560px; color: var(--text); }
    .de-hero h1 .drop-cap { font-size: 1.4em; color: var(--gold); }
    .de-hero-line { width: 80px; height: 2px; background: var(--gold); margin: 28px 0 24px; }
    .de-hero-sub { font-size: 1.1rem; color: var(--text-muted); max-width: 480px; margin-bottom: 36px; }
    .de-hero-cta { display: inline-block; padding: 15px 48px; background: transparent; border: 1px solid var(--gold); color: var(--gold); font-family: var(--font-heading); font-size: .78rem; text-transform: uppercase; letter-spacing: 3px; transition: background .3s, color .3s; }
    .de-hero-cta:hover { background: var(--gold); color: var(--bg); opacity: 1; }

    /* SECTIONS */
    .de-section { padding: 100px 0; }
    .de-section-alt { background: var(--bg-alt); }
    .label-tag { display: inline-block; font-family: var(--font-heading); font-size: .68rem; text-transform: uppercase; letter-spacing: 5px; color: var(--gold); margin-bottom: 12px; }
    .gold-ornament { display: flex; align-items: center; gap: 8px; margin-bottom: 28px; }
    .gold-ornament span:nth-child(1), .gold-ornament span:nth-child(3) { width: 32px; height: 1px; background: var(--gold); }
    .gold-ornament span:nth-child(2) { width: 6px; height: 6px; background: var(--gold); transform: rotate(45deg); }
    .section-title { font-family: var(--font-heading); font-size: clamp(1.5rem, 3vw, 2.2rem); font-weight: 400; text-transform: uppercase; letter-spacing: 3px; margin-bottom: 48px; color: var(--text); }

    /* ABOUT - full-bleed with semi-transparent overlay */
    .about-fullbleed { position: relative; min-height: 400px; display: flex; align-items: center; overflow: hidden; }
    .about-fullbleed-bg { position: absolute; inset: 0; }
    .about-fullbleed-bg img { width: 100%; height: 100%; object-fit: cover; display: block; }
    .about-fullbleed-overlay { position: absolute; inset: 0; background: rgba(12,0,21,.82); }
    .about-content { position: relative; z-index: 1; max-width: 700px; }
    .about-content h2 { font-family: var(--font-heading); font-size: 2rem; font-weight: 400; text-transform: uppercase; letter-spacing: 3px; margin-bottom: 24px; }
    .about-text { color: var(--text-muted); font-size: 1.05rem; line-height: 1.9; }

    /* SERVICES - single-col full-width list */
    .services-list { display: flex; flex-direction: column; gap: 0; }
    .service-row { display: flex; align-items: center; gap: 32px; padding: 32px 0; border-bottom: 1px solid var(--purple-dim); transition: background .3s; }
    .service-row:hover { background: rgba(126,34,206,.05); }
    .service-icon-area { flex-shrink: 0; }
    .service-icon-diamond { width: 48px; height: 48px; display: flex; align-items: center; justify-content: center; }
    .service-icon-diamond .icon-char { font-size: 24px; line-height: 1; color: var(--gold); }
    .service-details { flex: 1; }
    .service-details h3 { font-family: var(--font-heading); font-size: 1.1rem; text-transform: uppercase; letter-spacing: 2px; margin-bottom: 8px; color: var(--text); }
    .service-details p { color: var(--text-muted); font-size: .95rem; line-height: 1.7; }
    .service-price { display: inline-block; margin-top: 12px; color: var(--gold); font-family: var(--font-heading); font-size: 1.05rem; }

    /* GALLERY */
    .gallery-masonry { display: grid; grid-template-columns: repeat(auto-fit, minmax(220px, 1fr)); gap: 16px; }
    .gallery-cell img { width: 100%; height: 280px; object-fit: cover; border: 1px solid var(--purple-dim); transition: transform .3s, border-color .3s; }
    .gallery-cell img:hover { transform: scale(1.02); border-color: var(--gold); }

    /* TESTIMONIALS - horizontal scroll-snap carousel */
    .testimonials-carousel { display: flex; gap: 32px; overflow-x: auto; scroll-snap-type: x mandatory; padding-bottom: 16px; -webkit-overflow-scrolling: touch; }
    .testimonials-carousel::-webkit-scrollbar { height: 4px; }
    .testimonials-carousel::-webkit-scrollbar-thumb { background: var(--purple-dim); border-radius: 4px; }
    .quote-block { flex: 0 0 400px; scroll-snap-align: start; text-align: center; padding: 40px 24px; background: var(--bg); border: 1px solid var(--purple-dim); }
    .quote-decorative { font-family: var(--font-heading); font-size: 5rem; color: var(--gold); line-height: .6; opacity: .6; }
    .quote-decorative-end { font-family: var(--font-heading); font-size: 5rem; color: var(--gold); line-height: .5; opacity: .6; }
    .quote-block blockquote { font-size: 1.1rem; font-style: italic; color: var(--text-muted); line-height: 1.9; margin: 16px 0; }
    .quote-author strong { display: block; font-family: var(--font-heading); font-size: .85rem; text-transform: uppercase; letter-spacing: 2px; color: var(--text); margin-bottom: 4px; }
    .quote-author span { color: var(--text-muted); font-size: .85rem; }
    .q-stars { margin-top: 8px; }
    .star { color: #3a2050; font-size: .95rem; }
    .star.filled { color: var(--gold); }

    /* GOOGLE REVIEWS */
    .google-reviews { padding: 28px 0; text-align: center; border-bottom: 1px solid var(--purple-dim); }
    .review-badge { display: flex; align-items: center; justify-content: center; gap: 16px; flex-wrap: wrap; }
    .review-text { color: var(--text-muted); font-size: .9rem; }

    /* PROMO */
    .promo-banner { padding: 24px 0; background: linear-gradient(90deg, var(--purple), #5b21b6); text-align: center; }
    .promo-inner { display: flex; align-items: center; justify-content: center; gap: 16px; margin-bottom: 4px; }
    .promo-ornament { color: var(--gold); font-size: 1rem; }
    .promo-discount { font-family: var(--font-heading); font-weight: 700; color: var(--gold); text-transform: uppercase; letter-spacing: 3px; font-size: 1.1rem; }
    .promo-title { font-family: var(--font-heading); color: var(--text); font-size: .85rem; text-transform: uppercase; letter-spacing: 1px; }
    .promo-desc { color: rgba(243,232,255,.7); font-size: .85rem; }

    /* FAQ */
    .faq-grid { max-width: 720px; }
    .faq-entry { padding: 28px 0; border-bottom: 1px solid var(--purple-dim); }
    .faq-entry h3 { font-family: var(--font-heading); font-size: 1rem; letter-spacing: 1px; margin-bottom: 10px; color: var(--text); }
    .faq-q { color: var(--gold); margin-right: 4px; }
    .faq-entry p { color: var(--text-muted); font-size: .95rem; line-height: 1.8; }

    /* PRICE LIST */
    .pricing-grid { display: grid; grid-template-columns: repeat(auto-fit, minmax(260px, 1fr)); gap: 32px; }
    .price-card { background: var(--bg); border: 1px solid var(--purple-dim); padding: 32px 24px; text-align: center; position: relative; overflow: hidden; transition: border-color .3s; }
    .price-card:hover { border-color: var(--gold); }
    .price-card-gold-top { position: absolute; top: 0; left: 0; right: 0; height: 3px; background: var(--gold); }
    .price-card h3 { font-family: var(--font-heading); font-size: 1rem; text-transform: uppercase; letter-spacing: 2px; margin-bottom: 12px; color: var(--text); }
    .price-card-desc { color: var(--text-muted); font-size: .9rem; margin-bottom: 20px; line-height: 1.6; }
    .price-card-amount { font-family: var(--font-heading); font-size: 1.5rem; color: var(--gold); }

    /* HOURS */
    .hours-list { list-style: none; max-width: 400px; }
    .hours-list li { padding: 14px 0; border-bottom: 1px solid var(--purple-dim); color: var(--text-muted); font-size: .95rem; }

    /* CONTACT */
    .contact-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 64px; align-items: start; }
    .contact-info { display: flex; flex-direction: column; gap: 20px; }
    .contact-row { display: flex; align-items: center; gap: 12px; color: var(--text-muted); }
    .contact-row .icon-char { color: var(--gold); font-size: 20px; line-height: 1; flex-shrink: 0; }
    .contact-row a { color: var(--text); }
    .map-btn { display: inline-block; margin-top: 8px; font-family: var(--font-heading); font-size: .72rem; text-transform: uppercase; letter-spacing: 2px; }
    .de-form { display: flex; flex-direction: column; gap: 16px; }
    .de-form input, .de-form textarea { background: transparent; border: 1px solid var(--purple-dim); color: var(--text); padding: 14px 18px; font-family: var(--font-body); font-size: 1rem; outline: none; transition: border-color .2s; }
    .de-form input:focus, .de-form textarea:focus { border-color: var(--gold); }
    .de-form input::placeholder, .de-form textarea::placeholder { color: var(--text-muted); opacity: .5; }
    .de-form button { padding: 15px; border: 1px solid var(--gold); background: transparent; color: var(--gold); font-family: var(--font-heading); font-size: .75rem; text-transform: uppercase; letter-spacing: 3px; cursor: pointer; transition: background .3s, color .3s; }
    .de-form button:hover { background: var(--gold); color: var(--bg); }

    /* FOOTER */
    .de-footer { padding: 56px 0; text-align: center; border-top: 1px solid var(--purple-dim); }
    .de-footer-ornament { display: flex; align-items: center; justify-content: center; gap: 12px; margin-bottom: 20px; }
    .de-footer-ornament .fo-line { width: 50px; height: 1px; background: var(--gold); opacity: .4; }
    .de-footer-ornament .fo-diamond { width: 6px; height: 6px; background: var(--gold); transform: rotate(45deg); opacity: .4; }
    .de-footer p { color: var(--text-muted); font-size: .8rem; letter-spacing: 1px; }
    .de-footer .footer-brand { font-family: var(--font-heading); font-size: .9rem; color: var(--gold); text-transform: uppercase; letter-spacing: 4px; margin-bottom: 12px; }

    /* RESPONSIVE */
    @media (max-width: 768px) {
      .de-nav-links { display: none; flex-direction: column; position: absolute; top: 100%; left: 0; right: 0; background: rgba(12,0,21,.98); padding: 16px 24px; gap: 16px; }
      .de-nav-links.show { display: flex; }
      .de-nav-toggle { display: block; }
      .contact-grid { grid-template-columns: 1fr; gap: 32px; }
      .service-row { flex-direction: column; text-align: center; }
      .pricing-grid { grid-template-columns: 1fr; }
      .de-hero-bar-top, .de-hero-bar-bottom { height: 8vh; }
      .quote-block { flex: 0 0 300px; }
    }
    @media (max-width: 480px) {
      .de-hero-content { padding: 100px 16px 40px; }
      .de-section { padding: 64px 0; }
      .gallery-masonry { grid-template-columns: repeat(2, 1fr); }
    }
  </style>
</head>
<body>
  <nav class="de-nav">
    <div class="de-nav-inner">
      <a href="#" class="de-nav-brand">${safeName}</a>
      <button class="de-nav-toggle" aria-label="Toggle menu" onclick="this.parentElement.querySelector('.de-nav-links').classList.toggle('show')">
        <span></span><span></span><span></span>
      </button>
      <ul class="de-nav-links">
        ${navLinks.map(l => `<li><a href="${l.href}">${l.label}</a></li>`).join('')}
      </ul>
    </div>
  </nav>

  <section class="de-hero">
    <div class="de-hero-bar de-hero-bar-top"></div>
    <div class="de-hero-middle">
      <div class="de-hero-img">
        <img src="${escapeAttr(heroImage)}" alt="${safeName}">
      </div>
      <div class="de-hero-overlay"></div>
      <div class="de-hero-content">
        <h1><span class="drop-cap">${safeHeadline.charAt(0)}</span>${safeHeadline.slice(1)}</h1>
        <div class="de-hero-line"></div>
        <p class="de-hero-sub">${truncateAtSentence(safeAbout)}</p>
        ${enabledFeatures.has('contact-form') ? `<a href="#contact" class="de-hero-cta">Inquire Now</a>` : ''}
      </div>
    </div>
    <div class="de-hero-bar de-hero-bar-bottom"></div>
  </section>

  ${sections.join('\n')}

  <footer class="de-footer">
    <div class="container">
      <p class="footer-brand">${safeName}</p>
      <div class="de-footer-ornament">
        <span class="fo-line"></span><span class="fo-diamond"></span><span class="fo-line"></span>
      </div>
      <p>${safeName}</p>
    </div>
  </footer>
</body>
</html>`;
  }
};
